import { h, Host } from "@stencil/core";
import { GxControlDataType, GxControlType } from "../genexus";
/**
 * Represents a component that provides filtering controls for a grid column.
 */
// eslint-disable-next-line @stencil-community/required-prefix
export class GridChameleonColumnFilter {
    constructor() {
        this.column = undefined;
        this.buttonApplyText = undefined;
        this.buttonResetText = undefined;
        this.equal = undefined;
        this.less = undefined;
        this.greater = undefined;
    }
    filterEnum = [];
    inputEqual;
    inputLess;
    inputGreater;
    el;
    /**
     * Emitted when the filter settings for the column have changed.
     * This event carries the updated filter values.
     */
    columnSettingsChanged;
    componentWillLoad() {
        if (Array.isArray(this.column.FilterEnum) &&
            this.column.FilterEnum.length > 0) {
            this.filterEnum = this.column.FilterEnum;
        }
    }
    applyClickHandler = () => {
        this.equal = this.getControlValue(this.inputEqual);
        this.less = this.getControlValue(this.inputLess);
        this.greater = this.getControlValue(this.inputGreater);
        this.columnSettingsChanged.emit({
            column: this.column,
            equal: this.equal,
            less: this.less,
            greater: this.greater
        });
    };
    resetClickHandler = () => {
        this.equal = "";
        this.less = "";
        this.greater = "";
        this.columnSettingsChanged.emit({
            column: this.column,
            equal: this.equal,
            less: this.less,
            greater: this.greater
        });
    };
    getControlValue(input) {
        const value = input?.value ?? "";
        let dataType = this.column.gxControl.dataType;
        if (dataType === GxControlDataType.DATETIME &&
            this.column.FilterDateTimeAsDate === -1) {
            dataType = GxControlDataType.DATE;
        }
        switch (dataType) {
            case GxControlDataType.DATE:
                return gx.date.ctod(value, "Y4MD").toString();
            case GxControlDataType.DATETIME:
                return gx.date.ctot(value, "Y4MD").toString();
            default:
                return value;
        }
    }
    toControlValue(value) {
        let dataType = this.column.gxControl.dataType;
        if (!value) {
            return "";
        }
        if (dataType === GxControlDataType.DATETIME &&
            this.column.FilterDateTimeAsDate === -1) {
            dataType = GxControlDataType.DATE;
        }
        switch (dataType) {
            case GxControlDataType.DATE:
                return this.convertGxDateToISO(gx.date.ctod(value), false);
            case GxControlDataType.DATETIME:
                return this.convertGxDateToISO(gx.date.ctot(value), true);
            default:
                return value;
        }
    }
    convertGxDateToISO(gxdate, isDateTime) {
        const pad = (n) => n.toString().padStart(2, "0");
        const date = gxdate.Value;
        if (gx.date.isNullDate(date)) {
            return "";
        }
        if (isDateTime) {
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
        }
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    }
    getFilterInputType(dataType) {
        switch (dataType) {
            case GxControlDataType.BOOLEAN:
                return "checkbox";
            case GxControlDataType.CHAR:
            case GxControlDataType.VARCHAR:
            case GxControlDataType.LONGVARCHAR:
                return "text";
            case GxControlDataType.DATE:
                return "date";
            case GxControlDataType.DATETIME:
                return "datetime-local";
            case GxControlDataType.NUMBER:
                return "number";
            default:
                return "text";
        }
    }
    renderColumnFilterControl(input, type, dataType, possibleValues, label, value) {
        const part = input.replace("input", "").toLowerCase();
        if (this.filterEnum.length > 0) {
            type = GxControlType.COMBO;
            possibleValues = this.filterEnum.map(filter => [
                filter.Value,
                filter.Description
            ]);
        }
        if (dataType === GxControlDataType.DATETIME &&
            this.column.FilterDateTimeAsDate === -1) {
            dataType = GxControlDataType.DATE;
        }
        switch (type) {
            case GxControlType.EDIT:
            case GxControlType.CHECK:
                return (h("label", { part: `label ${part}` }, label, h("input", { type: this.getFilterInputType(dataType), value: this.toControlValue(value), ref: el => (this[input] = el), part: `field ${part}` })));
            case GxControlType.COMBO:
                return (h("label", { part: `label ${part}` }, label, h("select", { ref: el => (this[input] = el), part: `field ${part}` }, possibleValues.map(([optionValue, optionDescription]) => (h("option", { value: optionValue, selected: optionValue === value }, optionDescription))))));
        }
    }
    render() {
        return (h(Host, { key: '06335d3b3c3c64bf3bff9dffa22229fdf8063a03' }, h("fieldset", { key: '1f4bcf4343b816b22ef6efaaaa11fc9221553162', part: "main" }, h("caption", { key: 'd3ffe889e557157ad180bc97a41005b16556420e', part: "caption" }, this.column.FilterCaption), this.column.FilterMode === "single" &&
            this.renderColumnFilterControl("inputEqual", this.column.gxControl.type, this.column.gxControl.dataType, this.column.gxControl.possibleValues, this.column.FilterLabelEqual, this.equal), this.column.FilterMode === "range" &&
            this.renderColumnFilterControl("inputGreater", this.column.gxControl.type, this.column.gxControl.dataType, this.column.gxControl.possibleValues, this.column.FilterLabelGreater, this.greater), this.column.FilterMode === "range" &&
            this.renderColumnFilterControl("inputLess", this.column.gxControl.type, this.column.gxControl.dataType, this.column.gxControl.possibleValues, this.column.FilterLabelLess, this.less)), h("section", { key: '930fbb1cd10e1c45c4cfd91857ca92ac6b2e9166', part: "footer" }, h("button", { key: 'fcc54fa5007f23cb7a89226a76108496dd15fc1a', part: "button reset", onClick: this.resetClickHandler }, this.buttonResetText), h("button", { key: '53c260e9d385284bfc45a6fa15d0992bc48e3bc6', part: "button apply", onClick: this.applyClickHandler }, this.buttonApplyText))));
    }
    static get is() { return "gx-grid-chameleon-column-filter"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["gx-grid-chameleon-column-filter.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["gx-grid-chameleon-column-filter.css"]
        };
    }
    static get properties() {
        return {
            "column": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "GxGridColumn",
                    "resolved": "GxGridColumn",
                    "references": {
                        "GxGridColumn": {
                            "location": "import",
                            "path": "../genexus",
                            "id": "src/components/gx-grid/genexus.ts::GxGridColumn"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The grid column associated with this filter."
                }
            },
            "buttonApplyText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The text to display on the \"Apply\" button."
                },
                "attribute": "button-apply-text",
                "reflect": false
            },
            "buttonResetText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The text to display on the \"Reset\" button."
                },
                "attribute": "button-reset-text",
                "reflect": false
            },
            "equal": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value to filter for equality comparison."
                },
                "attribute": "equal",
                "reflect": false
            },
            "less": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value to filter for less-than comparison."
                },
                "attribute": "less",
                "reflect": false
            },
            "greater": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value to filter for greater-than comparison."
                },
                "attribute": "greater",
                "reflect": false
            }
        };
    }
    static get events() {
        return [{
                "method": "columnSettingsChanged",
                "name": "columnSettingsChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the filter settings for the column have changed.\nThis event carries the updated filter values."
                },
                "complexType": {
                    "original": "GridChameleonColumnFilterChanged",
                    "resolved": "GridChameleonColumnFilterChanged",
                    "references": {
                        "GridChameleonColumnFilterChanged": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/gx-grid/gx-grid-column-filter/gx-grid-chameleon-column-filter.tsx",
                            "id": "src/components/gx-grid/gx-grid-column-filter/gx-grid-chameleon-column-filter.tsx::GridChameleonColumnFilterChanged"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
}
